import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { PLATFORMS } from "@/data/site";
import { OFundedIcon } from "@/components/BrandLogo";
import { CheckCircle2, Zap, Globe2, Layers, Cpu } from "lucide-react";

/* Per-platform editorial meta — strings reference i18n keys so
   the DetailPanel can resolve them with the current locale. */
const META = {
  ctrader:    { Icon: Zap,    keyBase: "platforms.meta.ctrader",    bulletCount: 3 },
  dxtrade:    { Icon: Globe2, keyBase: "platforms.meta.dxtrade",    bulletCount: 3 },
  matchtrader:{ Icon: Layers, keyBase: "platforms.meta.matchtrader", bulletCount: 3 },
  gooeypro:   { Icon: Cpu,    keyBase: "platforms.meta.gooeypro",   bulletCount: 3 },
};

/* ============================================================
   MOTHERBOARD — 5 equal tiles (OFunded centre + 4 platforms at
   N/E/S/W) interconnected by a dense web of orthogonal traces,
   plus many decorative traces ending in glowing terminal dots
   in the corner zones (matching the user's reference image).

   Key constants:
     SIZE       — viewBox edge length
     HALF       — viewBox centre
     TILE_HALF  — half-extent of every tile in viewBox units
                  (tiles are sized via CSS `width: 18%` of the
                   square container, so 18% × 1000 / 2 = 90)
     ORBIT      — distance from centre tile to satellite centre
                  Chose so that satellite_inner_edge - centre_edge
                  ≈ 130 vB units → room for 4–5 multi-jog traces.
   ============================================================ */
const SIZE = 1000;
const HALF = SIZE / 2;
const TILE_HALF = 90;
// ORBIT = SIZE / 3 → satellites land at the centres of a perfect 3×3
// grid (top-middle, middle-left, middle-right, middle-bottom cells),
// so SVG trace coords line up EXACTLY with the rendered tile centres.
const ORBIT = SIZE / 3;

const NODES = {
  center: { x: HALF, y: HALF },
  top:    { x: HALF, y: HALF - ORBIT },
  right:  { x: HALF + ORBIT, y: HALF },
  bottom: { x: HALF, y: HALF + ORBIT },
  left:   { x: HALF - ORBIT, y: HALF },
};

/* ============================================================
   PATH GENERATORS
   We design every path in the TOP-side local frame, then rotate
   90° / 180° / 270° around (HALF, HALF) for the other three
   sides — guaranteeing 4-way rotational symmetry.
   ============================================================ */
function rotatePath90(path) {
  return path.replace(/([ML])\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/g, (_, cmd, x, y) => {
    const nx = HALF - (parseFloat(y) - HALF);
    const ny = HALF + (parseFloat(x) - HALF);
    return `${cmd} ${nx.toFixed(2)} ${ny.toFixed(2)}`;
  });
}

/* 5 multi-jog paths from TOP satellite → CENTER. The 5 paths
   share start-points spread across the bottom edge of the top
   tile and end-points spread across the top edge of the centre
   tile, all routed through the 130-unit gap between them. */
function topToCenterPaths() {
  const t = NODES.top;
  const c = NODES.center;
  const startY = t.y + TILE_HALF;   // bottom edge of top tile (220 + 90 = 310 — wait t.y = 500-310=190; bottom edge = 280)
  const endY = c.y - TILE_HALF;     // top edge of centre tile (500-90=410)
  // (Re-derive cleanly: startY=280, endY=410, distance=130)
  const offsets = [
    { sx: -60, ex: -55, midSplit: 0.40 },
    { sx: -30, ex: -25, midSplit: 0.55 },
    { sx:   0, ex:   0, midSplit: 0.50 }, // umbilical straight
    { sx:  30, ex:  25, midSplit: 0.55 },
    { sx:  60, ex:  55, midSplit: 0.40 },
  ];
  return offsets.map(({ sx, ex, midSplit }) => {
    const x1 = t.x + sx;
    const x2 = c.x + ex;
    const midY = startY + (endY - startY) * midSplit;
    return `M ${x1} ${startY} L ${x1} ${midY} L ${x2} ${midY} L ${x2} ${endY}`;
  });
}

/* 2 corner-routing paths from TOP → RIGHT (going around the
   centre tile's TOP-RIGHT corner). After rotating these for the
   other 3 corners we have a total of 8 ring paths. */
function topToRightPaths() {
  const t = NODES.top;
  const r = NODES.right;
  // Start on RIGHT edge of top tile, end on TOP edge of right tile
  const startX = t.x + TILE_HALF;   // 590
  const startY = t.y;               // 190
  const endX = r.x;                 // 810
  const endY = r.y - TILE_HALF;     // 410
  // Two variations — different corner clearance
  const variants = [
    { rx: endX + 30, ry: startY - 20 }, // outer routing
    { rx: endX - 50, ry: startY + 35 }, // inner routing (closer to centre)
  ];
  return variants.map(({ rx, ry }) => {
    return `M ${startX} ${startY} L ${rx} ${startY} L ${rx} ${ry} L ${endX} ${ry} L ${endX} ${endY}`;
  });
}

/* Decorative traces ending in glowing TERMINAL DOTS, scattered
   in the TOP-LEFT corner region of the board. Each trace starts
   somewhere ~50–250 viewBox units in from the edge and ends in
   a dot placed near the viewBox corner. After rotation for the
   other 3 corners we get a dense organic web like the reference.
   Each returned object is { d, endX, endY } so we can render
   both the path AND its terminal dot at the correct location. */
function topLeftTerminalTraces() {
  // 8 small traces, all anchored to the top-left quadrant
  // (x < HALF, y < HALF), with right-angle jogs and terminal
  // glowing dots near the viewBox edges.
  const traces = [
    // [startX, startY, jog1X, jog1Y, jog2X, jog2Y, endX, endY]
    [200, 220, 200, 130, 130, 130, 130, 60],
    [240, 260, 180, 260, 180, 200, 80,  200],
    [180, 300, 180, 240, 90,  240, 90,  110],
    [260, 180, 260, 100, 220, 100, 220, 50],
    [220, 320, 100, 320, 100, 290, 40,  290],
    [200, 380, 60,  380, 60,  330, 60,  330],
    [280, 350, 280, 280, 240, 280, 240, 220],
    [160, 360, 160, 290, 130, 290, 130, 220],
  ];
  return traces.map(([sx, sy, j1x, j1y, j2x, j2y, ex, ey]) => ({
    d: `M ${sx} ${sy} L ${j1x} ${j1y} L ${j2x} ${j2y} L ${ex} ${ey}`,
    endX: ex,
    endY: ey,
  }));
}

/* Rotate a terminal trace object by 90° clockwise (n times). */
function rotateTerminal(t, times) {
  let rotated = t;
  for (let i = 0; i < times; i++) {
    const newD = rotatePath90(rotated.d);
    const newEndX = HALF - (rotated.endY - HALF);
    const newEndY = HALF + (rotated.endX - HALF);
    rotated = { d: newD, endX: newEndX, endY: newEndY };
  }
  return rotated;
}

/* Build everything we need to render. */
function buildAllPaths() {
  // 4 fans of 5 multi-jog paths (5 × 4 = 20 paths total from
  // every satellite into OFunded)
  const fanTop = topToCenterPaths();
  const fanRight = fanTop.map(rotatePath90);
  const fanBottom = fanRight.map(rotatePath90);
  const fanLeft = fanBottom.map(rotatePath90);

  // 4 corner rings × 2 paths each = 8 ring paths interconnecting
  // adjacent satellites around OFunded
  const ringTR = topToRightPaths();
  const ringBR = ringTR.map(rotatePath90);
  const ringBL = ringBR.map(rotatePath90);
  const ringTL = ringBL.map(rotatePath90);

  // 4 corner zones × 8 terminal traces = 32 decorative traces
  // with glowing endpoint dots
  const termTL = topLeftTerminalTraces();
  const termTR = termTL.map((t) => rotateTerminal(t, 1));
  const termBR = termTL.map((t) => rotateTerminal(t, 2));
  const termBL = termTL.map((t) => rotateTerminal(t, 3));

  return {
    fans: { top: fanTop, right: fanRight, bottom: fanBottom, left: fanLeft },
    rings: [ringTR, ringBR, ringBL, ringTL].flat(),
    terminals: [...termTL, ...termTR, ...termBR, ...termBL],
  };
}

/* ============================================================
   Background "PCB city" — small IC chip block silhouettes
   placed symmetrically (4-way rotational).
   ============================================================ */
function MotherboardChipBlock({ x, y, w, h, rows, cols }) {
  const cellW = w / cols;
  const cellH = h / rows;
  return (
    <g opacity="0.5">
      {Array.from({ length: rows }).map((_, r) =>
        Array.from({ length: cols }).map((__, c) => (
          <rect key={`${r}-${c}`}
            x={x + c * cellW + cellW * 0.18}
            y={y + r * cellH + cellH * 0.18}
            width={cellW * 0.64}
            height={cellH * 0.64}
            fill="#AADEEB"
            fillOpacity="0.55"
          />
        ))
      )}
    </g>
  );
}

// Top-left quadrant block definitions; rotated for other 3 quadrants.
const TL_BLOCKS = [
  // [x, y, w, h, rows, cols]
  [60,  80,  26, 14, 2, 4],
  [160, 50,  20, 20, 3, 3],
  [50,  240, 30, 16, 2, 4],
  [180, 180, 22, 22, 3, 3],
  [120, 320, 24, 12, 2, 4],
  [80,  380, 20, 20, 2, 3],
  [220, 100, 22, 14, 2, 3],
  [40,  150, 18, 18, 2, 2],
];

function rotateBlock([x, y, w, h, rows, cols], times) {
  // Rotate top-left corner (x, y) around (HALF, HALF) by 90° n times.
  // After rotation, the block's top-left differs — recompute.
  let bx = x, by = y, bw = w, bh = h;
  for (let i = 0; i < times; i++) {
    const newX = HALF - (by + bh - HALF);
    const newY = HALF + (bx - HALF);
    bx = newX;
    by = newY;
    [bw, bh] = [bh, bw];
  }
  return [bx, by, bw, bh, rows, cols];
}

function MotherboardBackground() {
  return (
    <g>
      {[0, 1, 2, 3].flatMap((q) =>
        TL_BLOCKS.map((b, i) => {
          const [x, y, w, h, rows, cols] = rotateBlock(b, q);
          return <MotherboardChipBlock key={`b-${q}-${i}`} x={x} y={y} w={w} h={h} rows={rows} cols={cols} />;
        })
      )}
    </g>
  );
}

/* ============================================================
   Render layers: rings + fans + terminals + pulses
   ============================================================ */
function MotherboardTraces({ activeId, platforms }) {
  const paths = useMemo(() => buildAllPaths(), []);
  const fanKeys = ["top", "right", "bottom", "left"];

  return (
    <>
      {/* Ring paths — faint, just decorative */}
      {paths.rings.map((d, i) => (
        <motion.path
          key={`ring-${i}`}
          d={d}
          fill="none"
          stroke="#AADEEB"
          strokeOpacity={0.32}
          strokeWidth={1.0}
          strokeLinecap="square"
          strokeLinejoin="miter"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.0, delay: 0.3 + (i % 4) * 0.1, ease: "easeOut" }}
        />
      ))}

      {/* Terminal traces with glowing END dots */}
      {paths.terminals.map((t, i) => (
        <g key={`term-${i}`}>
          <motion.path
            d={t.d}
            fill="none"
            stroke="#AADEEB"
            strokeOpacity={0.38}
            strokeWidth={1.1}
            strokeLinecap="square"
            strokeLinejoin="miter"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.4 + (i % 8) * 0.06, ease: "easeOut" }}
          />
          {/* glowing terminal dot — soft halo + bright core */}
          <circle cx={t.endX} cy={t.endY} r="6" fill="#AADEEB" fillOpacity="0.16" />
          <circle cx={t.endX} cy={t.endY} r="3" fill="#AADEEB" fillOpacity="0.55" />
          <circle cx={t.endX} cy={t.endY} r="1.4" fill="#E8F6FB"
                  style={{ filter: "drop-shadow(0 0 4px #AADEEB)" }} />
        </g>
      ))}

      {/* FAN paths — 5 multi-jog paths per side connecting satellite → centre.
          Highlight the fan whose platform is currently active. */}
      {fanKeys.map((side, sideIdx) => {
        const slot = platforms[sideIdx];
        const isActive = activeId === slot?.id;
        return (
          <g key={`fan-${side}`}>
            {paths.fans[side].map((d, i) => (
              <g key={`f-${side}-${i}`}>
                {/* soft "copper" rail behind */}
                <path d={d} fill="none" stroke="#AADEEB" strokeOpacity={isActive ? 0.16 : 0.07}
                      strokeWidth={isActive ? 5 : 3} strokeLinecap="round" strokeLinejoin="round" />
                {/* bright fg line */}
                <motion.path
                  d={d}
                  fill="none"
                  stroke="#AADEEB"
                  strokeOpacity={isActive ? 0.92 : 0.55}
                  strokeWidth={isActive ? 1.8 : 1.2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.9, delay: 0.6 + sideIdx * 0.08 + i * 0.06, ease: "easeOut" }}
                />
                {/* small bead at the centre-tile end of each path */}
                <circle cx={parseFloat(d.split(" ").slice(-2)[0])}
                        cy={parseFloat(d.split(" ").slice(-2)[1])}
                        r="2"
                        fill="#AADEEB"
                        fillOpacity={isActive ? 1 : 0.7} />
              </g>
            ))}
            {/* travelling pulse only on the centre (umbilical) path */}
            <g>
              {[0, 1.2, 2.4].map((delay) => (
                <circle
                  key={delay}
                  r={isActive ? 3.6 : 2.6}
                  fill="#AADEEB"
                  style={{ filter: "drop-shadow(0 0 6px #AADEEB)" }}
                >
                  <animateMotion dur="3.6s" repeatCount="indefinite" begin={`${delay}s`}
                                 path={paths.fans[side][2]} />
                  <animate attributeName="opacity" values="0;1;1;0" dur="3.6s"
                           repeatCount="indefinite" begin={`${delay}s`} />
                </circle>
              ))}
            </g>
          </g>
        );
      })}
    </>
  );
}

/* ============================================================
   Constellation — 5 equal tiles laid out on a strict 3×3 CSS
   grid. Each tile sits at the centre of its grid cell, so the
   tile positions are mathematically locked to a uniform grid.
   - Row 1: empty · TOP tile · empty
   - Row 2: LEFT tile · OFunded · RIGHT tile
   - Row 3: empty · BOTTOM tile · empty
   The SVG trace layer overlays the same square container and
   uses NODES.{top,right,bottom,left,center} which match the
   grid cell centres exactly (ORBIT = SIZE / 3).
   ============================================================ */
function Constellation({ activeId, onSelect }) {
  // Map PLATFORMS to grid cells in the visual order: top, right, bottom, left.
  // gridArea uses "row col" syntax (1-indexed).
  const SLOT_KEYS = ["top", "right", "bottom", "left"];
  const GRID_AREAS = {
    top:    "1 / 2 / 2 / 3",
    right:  "2 / 3 / 3 / 4",
    bottom: "3 / 2 / 4 / 3",
    left:   "2 / 1 / 3 / 2",
  };
  const positions = useMemo(
    () => PLATFORMS.map((p, i) => ({ ...p, ...NODES[SLOT_KEYS[i % 4]], slotKey: SLOT_KEYS[i % 4] })),
    []
  );

  return (
    <div
      className="relative w-full max-w-[680px] mx-auto aspect-square"
      data-testid="platform-constellation"
    >
      {/* SVG layer (full container) — PCB grid + chip blocks + traces */}
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <pattern id="pcbGrid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#AADEEB" strokeOpacity="0.04" strokeWidth="0.8" />
          </pattern>
          <radialGradient id="hubGlow" cx="50%" cy="50%" r="35%">
            <stop offset="0%" stopColor="#AADEEB" stopOpacity="0.16" />
            <stop offset="60%" stopColor="#AADEEB" stopOpacity="0.04" />
            <stop offset="100%" stopColor="#AADEEB" stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect width={SIZE} height={SIZE} fill="url(#pcbGrid)" />
        <circle cx={HALF} cy={HALF} r="200" fill="url(#hubGlow)" />

        <MotherboardBackground />
        <MotherboardTraces activeId={activeId} platforms={positions} />
      </svg>

      {/* HTML tile layer — explicit 3×3 CSS Grid so every tile is
          mathematically aligned to a cell centre. No more `left/top`
          percent positioning; layout is impossible to misalign. */}
      <div
        className="absolute inset-0 grid"
        style={{
          gridTemplateColumns: "1fr 1fr 1fr",
          gridTemplateRows: "1fr 1fr 1fr",
        }}
      >
        {/* OFunded — centre cell (row 2 / col 2) */}
        <div className="grid place-items-center relative" style={{ gridArea: "2 / 2 / 3 / 3" }}>
          <Tile isCentre testId="platform-tile-ofunded">
            <OFundedIcon style={{ width: "60%", height: "60%" }} />
          </Tile>
        </div>

        {/* 4 platform satellite tiles, each pinned to a grid cell */}
        {positions.map((p, i) => {
          const meta = META[p.id] || META.ctrader;
          const Icon = meta.Icon;
          const isActive = activeId === p.id;
          // Label sits OUTSIDE the tile, on the side facing AWAY from
          // OFunded — so labels never land in the central row/column.
          const labelStyle = (() => {
            if (p.slotKey === "top")    return { top: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)" };
            if (p.slotKey === "bottom") return { bottom: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)" };
            if (p.slotKey === "left")   return { top: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)" };
            return { top: "calc(100% + 4px)", left: "50%", transform: "translateX(-50%)" };
          })();
          const labelEl = (
            <span
              className="absolute rounded font-semibold pointer-events-none text-center whitespace-nowrap"
              style={{
                ...labelStyle,
                color: isActive ? "#AADEEB" : "#cdd6da",
                background: "rgba(6, 9, 11, 0.78)",
                backdropFilter: "blur(2px)",
                padding: "2px 6px",
                fontSize: "clamp(8px, 2.2vw, 11px)",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                lineHeight: 1.1,
                border: isActive
                  ? "1px solid color-mix(in srgb, var(--brand) 45%, transparent)"
                  : "1px solid color-mix(in srgb, var(--brand) 18%, transparent)",
              }}
            >
              {p.name}
            </span>
          );
          return (
            <div
              key={p.id}
              className="grid place-items-center relative"
              style={{ gridArea: GRID_AREAS[p.slotKey] }}
            >
              <Tile
                isActive={isActive}
                onClick={() => onSelect(p.id)}
                onMouseEnter={() => onSelect(p.id)}
                testId={`platform-tile-${p.id}`}
                ariaLabel={`View ${p.name}`}
                delay={0.4 + i * 0.1}
                label={labelEl}
              >
                {p.logo ? (
                  <img src={p.logo} alt="" aria-hidden="true"
                       className="object-contain"
                       style={{ width: "60%", height: "60%" }}
                       draggable={false}
                       onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <Icon size={28} className="text-brand" />
                )}
              </Tile>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Tile — single uniform tile primitive. Positioning is handled
   by the parent grid cell; this component only renders the box.
   ============================================================ */
function Tile({ isActive, isCentre, onClick, onMouseEnter, children, label, testId, ariaLabel, delay = 0 }) {
  const accentStrength = isCentre ? 70 : isActive ? 75 : 32;
  const borderWidth = isCentre || isActive ? 1.5 : 1;
  const ringShadow = isCentre
    ? "0 0 0 6px color-mix(in srgb, var(--brand) 6%, transparent), 0 10px 28px color-mix(in srgb, var(--brand) 22%, transparent)"
    : isActive
    ? "0 0 0 4px color-mix(in srgb, var(--brand) 16%, transparent), 0 8px 24px color-mix(in srgb, var(--brand) 20%, transparent)"
    : "0 3px 12px color-mix(in srgb, var(--brand) 9%, transparent)";

  const commonStyle = {
    width: "54%",
    aspectRatio: "1",
    background: "linear-gradient(135deg, #0c1a20 0%, #06090b 100%)",
    border: `${borderWidth}px solid color-mix(in srgb, var(--brand) ${accentStrength}%, transparent)`,
    boxShadow: `inset 0 0 0 1px color-mix(in srgb, var(--brand) 8%, transparent), ${ringShadow}`,
    transition: "border 0.25s, box-shadow 0.25s",
  };

  // Centered logo (no internal absolute positioning — flex-grid centres it)
  const Logo = (
    <div className="absolute inset-0 grid place-items-center pointer-events-none">
      {children}
    </div>
  );

  if (isCentre) {
    return (
      <div
        data-testid={testId}
        className="relative rounded-2xl z-10"
        style={commonStyle}
      >
        {Logo}
        {label}
      </div>
    );
  }

  return (
    <motion.button
      type="button"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      initial={{ opacity: 0, scale: 0.6 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay, type: "spring", stiffness: 180 }}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.96 }}
      className="relative rounded-2xl outline-none"
      style={commonStyle}
      aria-label={ariaLabel}
    >
      {Logo}
      {label}
    </motion.button>
  );
}

function DetailPanel({ activeId }) {
  const { t } = useTranslation();
  const platform = PLATFORMS.find((p) => p.id === activeId) || PLATFORMS[0];
  const meta = META[platform.id] || META.ctrader;
  const Icon = meta.Icon;
  const bullets = Array.from({ length: meta.bulletCount }, (_, i) =>
    t(`${meta.keyBase}.bullets.${i}`)
  );
  return (
    <motion.div
      key={platform.id}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="card-beam p-6 lg:p-8 h-full flex flex-col"
      data-testid={`platform-detail-${platform.id}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="w-11 h-11 rounded-lg grid place-items-center"
          style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)" }}>
          <Icon size={18} className="text-brand" />
        </span>
        <div>
          <h3 className="text-2xl lg:text-3xl font-semibold text-[color:var(--text-1)] leading-tight">{platform.name}</h3>
          <span className="text-[10px] uppercase tracking-[0.18em] text-brand">{t(`${meta.keyBase}.tagline`)}</span>
        </div>
      </div>

      <p className="text-sm text-[color:var(--text-2)] font-light leading-relaxed">{t(`${meta.keyBase}.desc`)}</p>

      <ul className="mt-4 space-y-2">
        {bullets.map((b, i) => (
          <li key={`${platform.id}-bullet-${i}`} className="flex items-center gap-2 text-sm text-[color:var(--text-2)]">
            <CheckCircle2 size={14} className="text-brand shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-5 grid grid-cols-2 gap-3 text-center">
        <div className="rounded-lg py-2.5 px-3"
          style={{ background: "color-mix(in srgb, var(--brand) 4%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 15%, transparent)" }}>
          <div className="text-xs text-[color:var(--text-3)] uppercase tracking-widest">{t("showcase.funding_rules", "Funding rules")}</div>
          <div className="text-sm text-[color:var(--text-1)] font-medium mt-0.5">{t("showcase.identical", "Identical")}</div>
        </div>
        <div className="rounded-lg py-2.5 px-3"
          style={{ background: "color-mix(in srgb, var(--brand) 4%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 15%, transparent)" }}>
          <div className="text-xs text-[color:var(--text-3)] uppercase tracking-widest">{t("showcase.account_gains", "Account gains")}</div>
          <div className="text-sm text-brand font-medium mt-0.5">{t("showcase.up_to_90", "Up to 90%")}</div>
        </div>
      </div>
    </motion.div>
  );
}

export default function PlatformShowcase() {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState(PLATFORMS[0]?.id || "ctrader");
  // Once the user clicks ANYTHING on the motherboard we stop the
  // auto-rotation permanently — they've signalled they want to drive
  // the selection themselves. Until then we cycle every 3.5s so the
  // section feels alive.
  const userInteractedRef = useRef(false);
  useEffect(() => {
    const id = setInterval(() => {
      if (userInteractedRef.current) return;
      setActiveId((cur) => {
        const idx = PLATFORMS.findIndex((p) => p.id === cur);
        return PLATFORMS[(idx + 1) % PLATFORMS.length].id;
      });
    }, 3500);
    return () => clearInterval(id);
  }, []);
  const handleSelect = (id) => {
    userInteractedRef.current = true;
    setActiveId(id);
  };

  return (
    <section id="platforms" data-testid="platform-showcase" className="relative py-20 sm:py-24 lg:py-32 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-10 sm:mb-12">
          <div className="pill-mono inline-flex mb-5">{t("platforms.pill", "Trade Anywhere")}</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight mb-4">
            {t("platforms.title", "Four institutional")}{" "}
            <span className="text-gradient-brand font-semibold">{t("platforms.titleAccent", "platforms — one account")}</span>
          </h2>
          <p className="text-[color:var(--text-3)] max-w-2xl mx-auto text-sm lg:text-base font-light">
            {t("platforms.subtitle", "Pick the venue that suits your strategy. Identical rules, identical payouts — across all four.")}
          </p>
        </div>

        {/* Pills — ABOVE the circuit, centred, generous spacing */}
        <div className="mb-12 sm:mb-16 flex flex-wrap items-center justify-center gap-2.5 sm:gap-3" data-testid="platform-pills">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handleSelect(p.id)}
              data-testid={`platform-pill-${p.id}`}
              className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-full transition-all uppercase tracking-wider font-semibold ${
                activeId === p.id
                  ? "bg-brand text-[#071116] shadow-[0_1px_3px_rgba(170,222,235,0.05)]"
                  : "bg-white/[0.04] text-[color:var(--text-1)] hover:text-brand border border-[color:var(--line-strong)] hover:border-brand/60"
              }`}
            >
              {p.logo && (
                <img src={p.logo} alt="" className="w-6 h-6 object-contain" draggable={false} />
              )}
              {p.name}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-center">
          <div>
            <Constellation activeId={activeId} onSelect={handleSelect} />
          </div>
          <div>
            <DetailPanel activeId={activeId} />
          </div>
        </div>
      </div>
    </section>
  );
}
